import { X, ChevronsUpDown, Bookmark } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FilterPanelShell,
  FilterSection,
  FilterChipRow,
  FilterChipMulti,
  GameChipsFilter,
  LanguageMultiSelectSection,
  PriceRangeSection,
} from '@/components/filters';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';
import { useState, useEffect } from 'react';
import { LocationAutocomplete, LocationValue } from '@/components/ui/location-autocomplete';
import { GradingCompanyCombobox } from '@/components/shared/GradingCompanyCombobox';
import { DISTANCE_OPTIONS, DistanceOption } from '@/lib/distanceUtils';
import { useAuth } from '@/hooks/useAuth';
import {
  SINGLES_CONDITION_LABELS,
  SEALED_CONDITION_LABELS,
  CATEGORY_LABELS,
  PRODUCT_TYPE_LABELS,
  ListingCategory,
  SinglesCondition,
  SealedCondition,
} from '@/types/listing';

type FilterMode = 'browse' | 'my-items';

interface InventoryFilterPanelProps {
  mode?: FilterMode;
  category: string[];
  setCategory: (value: string[]) => void;
  condition: string;
  setCondition: (value: string) => void;
  priceMin: number;
  setPriceMin: (value: number) => void;
  priceMax: number;
  setPriceMax: (value: number) => void;
  location: LocationValue | null;
  setLocation: (value: LocationValue | null) => void;
  distanceRadius: DistanceOption;
  setDistanceRadius: (value: DistanceOption) => void;
  includeShipping: boolean;
  setIncludeShipping: (value: boolean) => void;
  gradingCompany: string;
  setGradingCompany: (value: string) => void;
  gradeMin: number;
  setGradeMin: (value: number) => void;
  gradeMax: number;
  setGradeMax: (value: number) => void;
  premiumOnly: boolean;
  setPremiumOnly: (value: boolean) => void;
  productType: string;
  setProductType: (value: string) => void;
  selectedGame: string;
  setSelectedGame: (value: string) => void;
  selectedLanguages: string[];
  setSelectedLanguages: (value: string[]) => void;
  visibility?: string[];
  setVisibility?: (value: string[]) => void;
  availableGames?: string[];
  availableCategories?: string[];
  availableConditions?: string[];
  availableLanguages?: string[];
  availableGradingCompanies?: string[];
  availableProductTypes?: string[];
  hasSlabs?: boolean;
  onClearFilters: () => void;
}

// Animated reveal wrapper
function AnimatedFilterSection({ show, children }: { show: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'overflow-hidden transition-all duration-300 ease-in-out',
        show ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
      )}
    >
      {children}
    </div>
  );
}

function DistanceSlider({
  value,
  onChange,
  disabled,
}: {
  value: DistanceOption;
  onChange: (value: DistanceOption) => void;
  disabled?: boolean;
}) {
  const valueIndex = DISTANCE_OPTIONS.findIndex((opt) => opt.value === value);
  const currentIndex = valueIndex === -1 ? DISTANCE_OPTIONS.length - 1 : valueIndex;

  const handleChange = (values: number[]) => {
    onChange(DISTANCE_OPTIONS[values[0]].value);
  };

  const currentLabel = DISTANCE_OPTIONS[currentIndex]?.label || 'Any distance';

  return (
    <div className="px-2 pt-2" style={{ touchAction: 'none' }}>
      <Slider
        min={0}
        max={DISTANCE_OPTIONS.length - 1}
        step={1}
        value={[currentIndex]}
        onValueChange={handleChange}
        disabled={disabled}
        className="w-full"
      />
      <div className="flex justify-center text-xs text-muted-foreground/80 mt-2">
        <span className="font-medium">{currentLabel}</span>
      </div>
    </div>
  );
}

const VISIBILITY_OPTIONS = [
  { value: 'sold', label: 'Sold' },
  { value: 'priced', label: 'Priced' },
  { value: 'taking_offers', label: 'Taking Offers' },
  { value: 'private', label: 'Unlisted' },
];

function StatusMultiSelect({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  const triggerLabel =
    value.length === 0
      ? 'All Statuses'
      : value.length === 1
        ? VISIBILITY_OPTIONS.find((o) => o.value === value[0])?.label ?? '1 selected'
        : `${value.length} selected`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between h-9 !bg-[hsl(var(--toolbar-control-bg,var(--background)))]', toolbarControl)}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className={cn('w-4 h-4 shrink-0 opacity-50 transition-colors', open && 'opacity-100 text-foreground')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2 z-50 dropdown-surface border-white/[0.08]" align="start">
        <div className="space-y-1">
          {VISIBILITY_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm hover:bg-white/[0.05]',
                value.includes(opt.value) && 'bg-white/[0.08]'
              )}
              onClick={() => toggle(opt.value)}
            >
              <Checkbox checked={value.includes(opt.value)} className="h-4 w-4" />
              <span>{opt.label}</span>
            </div>
          ))}
        </div>
        {value.length > 0 && (
          <>
            <Separator className="my-2 bg-white/[0.08]" />
            <Button variant="ghost" size="sm" onClick={() => onChange([])} className="w-full text-xs">
              Clear Status
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function InventoryFilterPanel({
  mode = 'browse',
  category,
  setCategory,
  condition,
  setCondition,
  priceMin,
  setPriceMin,
  priceMax,
  setPriceMax,
  location,
  setLocation,
  distanceRadius,
  setDistanceRadius,
  includeShipping,
  setIncludeShipping,
  gradingCompany,
  setGradingCompany,
  gradeMin,
  setGradeMin,
  gradeMax,
  setGradeMax,
  premiumOnly,
  setPremiumOnly,
  productType,
  setProductType,
  selectedGame,
  setSelectedGame,
  selectedLanguages,
  setSelectedLanguages,
  visibility,
  setVisibility,
  availableGames,
  availableCategories,
  availableConditions,
  availableLanguages,
  availableGradingCompanies,
  availableProductTypes,
  hasSlabs,
  onClearFilters,
}: InventoryFilterPanelProps) {
  const isMyItemsMode = mode === 'my-items';
  // Condition/product-type sub-filters only make sense when exactly one category is picked.
  const catSel = category.length === 1 ? category[0] : 'all';

  const hasActiveFilters = isMyItemsMode
    ? ((visibility && visibility.length > 0) || category.length > 0 || priceMin !== 0 || priceMax !== 1000 || condition !== 'all' || selectedGame !== 'all' || selectedLanguages.length > 0)
    : (category.length > 0 ||
      condition !== 'all' ||
      priceMin !== 0 ||
      priceMax !== 1000 ||
      location !== null ||
      distanceRadius !== 25 ||
      !includeShipping ||
      gradingCompany !== 'all' ||
      gradeMin !== 1 ||
      gradeMax !== 10 ||
      premiumOnly ||
      productType !== 'all' ||
      selectedGame !== 'all' ||
      selectedLanguages.length > 0);

  // Get the appropriate condition labels based on category
  const getConditionLabels = () => {
    if (catSel === 'singles') return SINGLES_CONDITION_LABELS;
    if (catSel === 'sealed') return SEALED_CONDITION_LABELS;
    return { ...SINGLES_CONDITION_LABELS, ...SEALED_CONDITION_LABELS };
  };

  const conditionLabels = getConditionLabels();

  useEffect(() => {
    if (condition !== 'all' && !(condition in conditionLabels)) {
      setCondition('all');
    }
  }, [category, condition, conditionLabels, setCondition]);

  // Game chips render via <GameChipsFilter /> with built-in "More games" expansion.

  const categoryOptions = [
    { value: 'all', label: 'All' },
    ...(Object.entries(CATEGORY_LABELS) as [ListingCategory, string][])
      .filter(([value]) => !availableCategories || availableCategories.includes(value))
      .map(([value, label]) => ({ value, label })),
  ];

  const productTypeOptions = [
    { value: 'all', label: 'All types' },
    ...Object.entries(PRODUCT_TYPE_LABELS)
      .filter(([value]) => !availableProductTypes || availableProductTypes.includes(value))
      .map(([value, label]) => ({ value, label })),
  ];

  let conditionOptions: { value: string; label: string }[];
  if (catSel === 'singles') {
    conditionOptions = [
      { value: 'all', label: 'All' },
      ...(Object.entries(SINGLES_CONDITION_LABELS) as [SinglesCondition, string][]).map(([value, label]) => ({ value, label })),
    ];
  } else if (catSel === 'sealed') {
    conditionOptions = [
      { value: 'all', label: 'All' },
      ...(Object.entries(SEALED_CONDITION_LABELS) as [SealedCondition, string][]).map(([value, label]) => ({ value, label })),
    ];
  } else {
    conditionOptions = [
      { value: 'all', label: 'All' },
      { value: 'mint', label: 'Mint' },
      { value: 'near_mint', label: 'Near Mint' },
      { value: 'lightly_played', label: 'Lightly Played' },
      { value: 'factory_sealed', label: 'Factory Sealed' },
      { value: 'sealed', label: 'Sealed' },
    ];
  }
  if (availableConditions) {
    conditionOptions = conditionOptions.filter(
      (o) => o.value === 'all' || availableConditions.includes(o.value),
    );
  }

  const showGameSection = !availableGames || availableGames.length > 0;
  const showCategorySection = categoryOptions.length > 2; // "All" + >1 real option
  const showConditionSection = catSel !== 'slabs' && conditionOptions.length > 1;
  const showProductTypeRow = catSel === 'sealed' && productTypeOptions.length > 2;
  const showGradingSections = hasSlabs !== false; // undefined = show (backwards compat)

  return (
    <FilterPanelShell hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters}>
      <div className="flex flex-col gap-5 p-5">
        {/* 1. Game */}
        {showGameSection && (
          <FilterSection title="Game">
            <GameChipsFilter value={selectedGame} onChange={setSelectedGame} availableGames={availableGames} />
          </FilterSection>
        )}

        {/* 2. Status (Inventory only) */}
        {isMyItemsMode && setVisibility && (
          <FilterSection title="Status">
            <StatusMultiSelect value={visibility || []} onChange={setVisibility} />
          </FilterSection>
        )}

        {/* 3. Category */}
        {showCategorySection && (
          <FilterSection title="Category">
            <FilterChipMulti options={categoryOptions.filter(o => o.value !== 'all')} value={category} onChange={setCategory} />
            <AnimatedFilterSection show={catSel === 'sealed' && showProductTypeRow}>
              <div className="pt-3">
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Product Type</h4>
                <FilterChipRow options={productTypeOptions} value={productType} onChange={setProductType} />
              </div>
            </AnimatedFilterSection>
          </FilterSection>
        )}

        {/* 4. Language */}
        <LanguageMultiSelectSection
          value={selectedLanguages}
          onChange={setSelectedLanguages}
          availableLanguages={availableLanguages}
        />

        {/* 5. Condition (non-slabs) */}
        {showConditionSection && (
          <FilterSection title="Condition">
            <FilterChipRow options={conditionOptions} value={condition} onChange={setCondition} />
          </FilterSection>
        )}

        {/* 6. Grading company + grade range — only when the seller has slabs */}
        {showGradingSections && (
          <>
            <FilterSection title="Grading Company">
              <GradingCompanyCombobox
                value={gradingCompany === 'all' ? '' : gradingCompany}
                onChange={(value) => setGradingCompany(value || 'all')}
                placeholder="All Companies"
                allowedCompanies={availableGradingCompanies}
                variant="toolbar"
              />
            </FilterSection>
            <FilterSection title="Grade Range">
              <div className="px-2 pt-2" style={{ touchAction: 'none' }}>
                <Slider
                  min={1}
                  max={10}
                  step={0.5}
                  value={[gradeMin, gradeMax]}
                  onValueChange={(values) => {
                    setGradeMin(values[0]);
                    setGradeMax(values[1]);
                  }}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground/80 mt-2">
                  <span>{gradeMin}</span>
                  <span>{gradeMax}</span>
                </div>
              </div>
            </FilterSection>
          </>
        )}

        {/* 7. Price */}
        <PriceRangeSection
          min={priceMin}
          max={priceMax}
          cap={1000}
          onChange={({ min, max }) => {
            setPriceMin(min);
            setPriceMax(max);
          }}
        />

        {/* 8. Location (browse only) */}
        {!isMyItemsMode && (
          <FilterSection title="Location">
            <div className="space-y-2">
              <LocationAutocomplete
                value={location}
                onChange={setLocation}
                placeholder="Enter your city"
              />
              {location && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLocation(null)}
                  className="text-xs text-muted-foreground h-7 px-2"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear location
                </Button>
              )}

              <AnimatedFilterSection show={!!location}>
                <div className="pt-2">
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Distance</h4>
                  <DistanceSlider
                    value={distanceRadius}
                    onChange={setDistanceRadius}
                    disabled={!location}
                  />
                </div>
              </AnimatedFilterSection>

              <div className="flex items-center space-x-2 pt-1">
                <Checkbox
                  id="include-shipping"
                  checked={includeShipping}
                  onCheckedChange={(checked) => setIncludeShipping(checked === true)}
                />
                <Label htmlFor="include-shipping" className="text-sm font-medium cursor-pointer">
                  Include sellers who ship
                </Label>
              </div>
            </div>
          </FilterSection>
        )}

      </div>
    </FilterPanelShell>
  );
}

